import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Avatar,
  Chip,
  IconButton,
  Alert,
  TextField,
  Tab,
  Tabs,
  Stack,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Badge,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  EventBusy as EventBusyIcon,
  Add as AddIcon,
  CalendarToday as CalendarIcon,
  CheckCircle as CheckCircleIcon,
  Group as GroupIcon,
  Warning as WarningIcon,
  Edit,
  ExpandMore as ExpandMoreIcon,
  FilterList as FilterListIcon
} from '@mui/icons-material';
import moment from 'moment';
import { Employee, Schedule } from '../../../pages/Dashboard/Dashboard';
import SwapShiftModal from '../SwapShiftModal/SwapShiftModal';
import RequestOffModal from '../RequestOffModal/RequestOffModal';
import ShiftCard from '../../ShiftCard/ShiftCard';
import { deleteShift, putShiftCancel } from '../../../lib/connector/shiftQueries';
import { checkAccessLevel } from '../../../lib/authenticate';
import { useToast } from '../ToastProvider/ToastProvider';

interface Shift {
  shiftName: string;
  shiftId: string;
  notes: string;
  assignedStaff: string[];
  staffNeeded: string;
  startTime: string; // "HH:mm" format
  endTime: string; // "HH:mm" format
  position: string; // position ID
  isRecurring: boolean;
  daysOfWeek?: string[];
  scheduleId: string;
  shiftDate?: string; // exists on cancel shifts
  shiftReferenceId?: string; // exists on cancel shifts
}

interface EmployeeDayModalProps {
  open: boolean;
  onClose: () => void;
  date: string; // ISO date string
  shifts: Shift[];
  allShifts: Shift[];
  setShifts: React.Dispatch<React.SetStateAction<any[]>>;
  setShiftsForDay: React.Dispatch<React.SetStateAction<any[]>>;
  currentEmployeeId: string;
  userData: any;
  selectedShift: Shift | null;
  setSelectedShift: React.Dispatch<React.SetStateAction<Shift | null>>;
  setShowManageShiftModal: React.Dispatch<React.SetStateAction<boolean>>;
  employees: Employee[]; // All employees in the system
  positions: Record<string, any>[]; // Position lookup
  onPickUpShift?: (shiftId: string) => void;
  onRequestSwap?: (shiftId: string, targetEmployeeId: string, reason: string, isPermanent: boolean, swapDate: string) => void;
  onRequestTimeOff?: (shiftId: string, date: string, reason: string) => void;
  selectedSchedule: Schedule | null;
  accessLevel: string | null;
  assignments: Record<string, any>[]
}

const EmployeeDayModal: React.FC<EmployeeDayModalProps> = ({
  open,
  onClose,
  date,
  shifts = [],
  allShifts = [],
  currentEmployeeId,
  userData,
  selectedShift,
  setSelectedShift,
  setShifts,
  setShiftsForDay,
  setShowManageShiftModal,
  employees = [],
  positions = [],
  onPickUpShift,
  onRequestSwap,
  onRequestTimeOff,
  selectedSchedule,
  accessLevel,
  assignments
}) => {
  const toast = useToast();
  const [activeTab, setActiveTab] = useState(1);
  const [showSwapModal, setShowSwapModal] = useState(false);
  const [showRequestOffModal, setShowRequestOffModal] = useState(false);
  const [showPickUpDialog, setShowPickUpDialog] = useState(false);
  const [shiftToPickUp, setShiftToPickUp] = useState<Shift | null>(null);
  const [showRevertCancelDialog, setShowRevertCancelDialog] = useState(false);
  const [shiftToRevertCancel, setShiftToRevertCancel] = useState<Shift | null>(null);
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);
  const [shiftToDelete, setShiftToDelete] = useState<Shift | null>(null);
  
  // Search and filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPosition, setSelectedPosition] = useState('');
  const [startTimeFilter, setStartTimeFilter] = useState('');
  const [endTimeFilter, setEndTimeFilter] = useState('');
  const [assignmentLevelFilter, setAssignmentLevelFilter] = useState('');
  const [filterAccordionExpanded, setFilterAccordionExpanded] = useState(false);

  React.useEffect(() => {
    setActiveTab(checkAccessLevel(accessLevel) ? 0 : 1);
  }, [accessLevel])

  // Helper function to get employee by ID
  const getEmployee = (employeeId: string) => {
    return employees.find(e => e.userId === employeeId);
  };

  // Helper function to get position by ID
  const getPosition = (positionId: string) => {
    return positions.find(p => p.position === positionId);
  };

  // Categorize shifts
  const myShifts = shifts.filter(s => s.assignedStaff.includes(currentEmployeeId));

  const handleConfirmPickUp = () => {
    if (shiftToPickUp && onPickUpShift) {
      onPickUpShift(shiftToPickUp.shiftId);
      setShowPickUpDialog(false);
      setShiftToPickUp(null);
      onClose();
    }
  };

  const handleSwapSubmit = (shiftId: string, targetEmployeeId: string, reason: string, isPermanent: boolean, swapDate: string) => {
    if (onRequestSwap) {
      onRequestSwap(shiftId, targetEmployeeId, reason, isPermanent, swapDate);
      onClose();
    }
  };

  const handleRequestTimeOff = (shiftId: string, offDate: string, offReason: string) => {
    if (onRequestTimeOff) {
      onRequestTimeOff(shiftId, offDate, offReason);
      setShowRequestOffModal(false);
      setSelectedShift(null);
      onClose();
    }
  };

  const getShiftDuration = (shift: Shift) => {
    const [startHour, startMin] = shift.startTime.split(':').map(Number);
    const [endHour, endMin] = shift.endTime.split(':').map(Number);

    const start = moment().hours(startHour).minutes(startMin);
    const end = moment().hours(endHour).minutes(endMin);

    // Handle overnight shifts
    if (end.isBefore(start)) {
      end.add(1, 'day');
    }

    const duration = moment.duration(end.diff(start));
    return `${Math.floor(duration.asHours())}h ${duration.minutes()}m`;
  };

  const formatShiftTime = (timeStr: string) => {
    const [hour, min] = timeStr.split(':').map(Number);
    return moment().hours(hour).minutes(min).format('h:mm A');
  };

  const getSpotsRemaining = (shift: Shift) => {
    return parseInt(shift.staffNeeded) - shift.assignedStaff.length;
  };

  // Check if picking up this shift would create conflicts
  const hasConflictWithMyShifts = (newShift: Shift) => {
    return myShifts.some(myShift => {
      // Check for time overlap
      const newStart = moment().hours(parseInt(newShift.startTime.split(':')[0])).minutes(parseInt(newShift.startTime.split(':')[1]));
      const newEnd = moment().hours(parseInt(newShift.endTime.split(':')[0])).minutes(parseInt(newShift.endTime.split(':')[1]));
      const myStart = moment().hours(parseInt(myShift.startTime.split(':')[0])).minutes(parseInt(myShift.startTime.split(':')[1]));
      const myEnd = moment().hours(parseInt(myShift.endTime.split(':')[0])).minutes(parseInt(myShift.endTime.split(':')[1]));

      // Handle overnight shifts
      if (newEnd.isBefore(newStart)) newEnd.add(1, 'day');
      if (myEnd.isBefore(myStart)) myEnd.add(1, 'day');

      // Check if times overlap
      return (newStart.isBefore(myEnd) && newEnd.isAfter(myStart));
    });
  };

  // Helper function to get assignments for a specific shift on the given date
  const getShiftAssignmentForDate = (shiftId: string): Record<string, any>[] => {
    return assignments.filter((assignment: Record<string, any>) =>
      assignment.shiftId === shiftId && assignment.date === moment(date).format('YYYY-MM-DD')
    );
  };

  // Helper function to get assignees for a shift, prioritizing custom assignments
  const getShiftAssignees = (shift: Shift): string[] => {
    const customAssignment = getShiftAssignmentForDate(shift.shiftId);
    return customAssignment.length > 0 ? customAssignment[0].assignees : shift.assignedStaff;
  };

  // Categorize shifts using priority assignees
  const myShiftsWithPriority = shifts.filter(s => getShiftAssignees(s).includes(currentEmployeeId));
  const openShiftsWithPriority = shifts.filter(s =>
    parseInt(s.staffNeeded) > getShiftAssignees(s).length &&
    !getShiftAssignees(s).includes(currentEmployeeId) && (positions.filter(position => position.position === s.position
      && position.employees.includes(currentEmployeeId)).length > 0)
  );

  // Get canceled shifts for the current date
  const getCanceledShifts = (): Shift[] => {
    return allShifts.filter(shift => {
      return allShifts.find(s => s.shiftId === shift.shiftReferenceId && ((shift.shiftDate === date)));
    });
  };

  const canceledShifts = getCanceledShifts();

  // Check if the selected date is more than 2 days in the past
  const isDateTooOld = moment(date).isBefore(moment().subtract(2, 'days'), 'day');

  // Get assignment level for a shift
  const getAssignmentLevel = (shift: Shift): 'fully' | 'partial' | 'unassigned' => {
    const assignedCount = getShiftAssignees(shift).length;
    const neededCount = parseInt(shift.staffNeeded);
    
    if (assignedCount === 0) return 'unassigned';
    if (assignedCount >= neededCount) return 'fully';
    return 'partial';
  };

  // Filter shifts based on search and filters
  const filterShifts = (shiftsToFilter: Shift[]): Shift[] => {
    return shiftsToFilter.filter(shift => {
      // Search by shift name
      if (searchQuery && !shift.shiftName.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }

      // Filter by position
      if (selectedPosition && shift.position !== selectedPosition) {
        return false;
      }

      // Filter by start time
      if (startTimeFilter && shift.startTime !== startTimeFilter) {
        return false;
      }

      // Filter by end time
      if (endTimeFilter && shift.endTime !== endTimeFilter) {
        return false;
      }

      // Filter by assignment level
      if (assignmentLevelFilter) {
        const level = getAssignmentLevel(shift);
        if (level !== assignmentLevelFilter) {
          return false;
        }
      }

      return true;
    });
  };

  // Sort shifts alphabetically by name
  const sortShiftsByName = (shiftsToSort: Shift[]): Shift[] => {
    return [...shiftsToSort].sort((a, b) => a.shiftName.localeCompare(b.shiftName));
  };

  // Get unique positions and times for filter dropdowns
  const uniquePositions = Array.from(new Set(shifts.map(s => s.position))).filter(Boolean);
  const uniqueStartTimes = Array.from(new Set(shifts.map(s => s.startTime))).filter(Boolean).sort();
  const uniqueEndTimes = Array.from(new Set(shifts.map(s => s.endTime))).filter(Boolean).sort();

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
                <CalendarIcon />
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  {moment(date).format('dddd, MMMM D, YYYY')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {shifts.length} shift{shifts.length !== 1 ? 's' : ''} scheduled • {myShiftsWithPriority.length} assigned to you
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ pt: 0}}>
          <Tabs 
            value={activeTab} 
            onChange={(e, val) => {
              setActiveTab(val)
            }} 
            sx={{ mb: 3 }}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
          >
            {checkAccessLevel(accessLevel) && (
              <Tab
                label={
                  <Badge badgeContent={shifts.length} color="primary">
                    <Box sx={{ pr: shifts.length > 0 ? 2 : 0 }}>Today's Shifts</Box>
                  </Badge>
                }
                icon={<GroupIcon />}
                iconPosition="start"
                value={0}
              />
            )}
            <Tab
              label={
                <Badge badgeContent={myShiftsWithPriority.length} color="primary">
                  <Box sx={{ pr: myShiftsWithPriority.length > 0 ? 2 : 0 }}>My Shifts</Box>
                </Badge>
              }
              icon={<PersonIcon />}
              iconPosition="start"
              value={1}
            />
            <Tab
              label={
                <Badge badgeContent={openShiftsWithPriority.length} color="primary">
                  <Box sx={{ pr: openShiftsWithPriority.length > 0 ? 2 : 0 }}>Open Shifts</Box>
                </Badge>
              }
              icon={<AddIcon />}
              iconPosition="start"
              value={2}
            />
            {checkAccessLevel(accessLevel) && (
              <Tab
                label={
                  <Badge badgeContent={canceledShifts.length} color="primary">
                    <Box sx={{ pr: canceledShifts.length > 0 ? 2 : 0 }}>Canceled Shifts</Box>
                  </Badge>
                }
                icon={<EventBusyIcon />}
                iconPosition="start"
                value={3}
              />
            )}
          </Tabs>

          {/* Search and Filters Section - Accordion */}
          <Accordion 
            expanded={filterAccordionExpanded}
            onChange={(e, isExpanded) => setFilterAccordionExpanded(isExpanded)}
            sx={{ 
              mb: 3, 
              bgcolor: 'grey.50',
              position: 'sticky',
              top: 0,
              zIndex: 10,
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FilterListIcon fontSize="small" />
                <Typography variant="subtitle2" fontWeight={600}>
                  Search & Filter Shifts
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2} sx={{ width: '100%' }}>
                {/* Search Bar */}
                <TextField
                  fullWidth
                  placeholder="Search by shift name..."
                  size="small"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  variant="outlined"
                />

                {/* Filter Row */}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                  {/* Position Filter */}
                  <FormControl size="small" sx={{ minWidth: 150, flex: 1 }}>
                    <InputLabel>Position</InputLabel>
                    <Select
                      value={selectedPosition}
                      onChange={(e) => setSelectedPosition(e.target.value)}
                      label="Position"
                    >
                      <MenuItem value="">All Positions</MenuItem>
                      {uniquePositions.map(posId => (
                        <MenuItem key={posId} value={posId}>
                          {getPosition(posId)?.positionName || getPosition(posId)?.name || 'Unknown'}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Start Time Filter */}
                  <FormControl size="small" sx={{ minWidth: 130, flex: 1 }}>
                    <InputLabel>Start Time</InputLabel>
                    <Select
                      value={startTimeFilter}
                      onChange={(e) => setStartTimeFilter(e.target.value)}
                      label="Start Time"
                    >
                      <MenuItem value="">All Times</MenuItem>
                      {uniqueStartTimes.map(time => (
                        <MenuItem key={time} value={time}>
                          {formatShiftTime(time)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* End Time Filter */}
                  <FormControl size="small" sx={{ minWidth: 130, flex: 1 }}>
                    <InputLabel>End Time</InputLabel>
                    <Select
                      value={endTimeFilter}
                      onChange={(e) => setEndTimeFilter(e.target.value)}
                      label="End Time"
                    >
                      <MenuItem value="">All Times</MenuItem>
                      {uniqueEndTimes.map(time => (
                        <MenuItem key={time} value={time}>
                          {formatShiftTime(time)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Assignment Level Filter */}
                  <FormControl size="small" sx={{ minWidth: 160, flex: 1 }}>
                    <InputLabel>Assignment Level</InputLabel>
                    <Select
                      value={assignmentLevelFilter}
                      onChange={(e) => setAssignmentLevelFilter(e.target.value)}
                      label="Assignment Level"
                    >
                      <MenuItem value="">All Levels</MenuItem>
                      <MenuItem value="fully">Fully Assigned</MenuItem>
                      <MenuItem value="partial">Partially Assigned</MenuItem>
                      <MenuItem value="unassigned">Unassigned</MenuItem>
                    </Select>
                  </FormControl>

                  {/* Clear Filters Button */}
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      setSearchQuery('');
                      setSelectedPosition('');
                      setStartTimeFilter('');
                      setEndTimeFilter('');
                      setAssignmentLevelFilter('');
                    }}
                    sx={{ minWidth: 100 }}
                  >
                    Clear Filters
                  </Button>
                </Stack>
              </Stack>
            </AccordionDetails>
          </Accordion>

          {/* Team Schedule Tab */}
          {activeTab === 0 && (
            <Box>
              {shifts.length === 0 ? (
                <Alert severity="info" icon={<GroupIcon />}>
                  No other team members scheduled for this day.
                </Alert>
              ) : (
                <>
                  {(() => {
                    const filteredTeamShifts = sortShiftsByName(filterShifts(shifts));
                    return (
                      <>
                        {filteredTeamShifts.length === 0 && (
                          <Alert severity="info" sx={{ mb: 2 }}>
                            No shifts match your search and filter criteria.
                          </Alert>
                        )}
                        {filteredTeamShifts.length > 0 && (
                          <Alert severity="info" icon={<Edit />} sx={{ mb: 2 }}>
                            {filteredTeamShifts.length} shift{filteredTeamShifts.length !== 1 ? 's' : ''} found. Select a shift to make changes to it.
                          </Alert>
                        )}
                        {filteredTeamShifts.map(shift => (
                          <ShiftCard
                            key={shift.shiftId}
                            shift={shift}
                            type="other"
                            selectedSchedule={selectedSchedule}
                            currentEmployeeId={currentEmployeeId}
                            assignments={getShiftAssignees(shift)}
                            getEmployee={getEmployee}
                            getPosition={getPosition}
                            onClick={() => {
                              setSelectedShift(shift);
                              setShowManageShiftModal(true);
                            }}
                            showDeleteButton={true}
                            disabled={isDateTooOld}
                            onDelete={((shift: Shift) => {
                              setShiftToDelete(shift);
                              setShowDeleteConfirmDialog(true);
                            })}
                          />
                        ))}
                      </>
                    );
                  })()}
                </>
              )}
            </Box>
          )}

          {/* My Shifts Tab */}
          {activeTab === 1 && (
            <Box>
              {myShiftsWithPriority.length === 0 ? (
                <Alert severity="info" icon={<PersonIcon />}>
                  You have no shifts scheduled for this day. Check the "Open Shifts" tab to pick up available shifts.
                </Alert>
              ) : (
                <>
                  {(() => {
                    const filteredMyShifts = sortShiftsByName(filterShifts(myShiftsWithPriority));
                    return (
                      <>
                        {filteredMyShifts.length === 0 && (
                          <Alert severity="info" sx={{ mb: 2 }}>
                            No shifts match your search and filter criteria.
                          </Alert>
                        )}
                        {filteredMyShifts.length > 0 && (
                          <Alert severity="info" sx={{ mb: 2 }}>
                            {filteredMyShifts.length} shift{filteredMyShifts.length !== 1 ? 's' : ''} found.
                          </Alert>
                        )}
                        {filteredMyShifts.map(shift => (
                          <ShiftCard
                            key={shift.shiftId}
                            shift={shift}
                            type="my"
                            selectedSchedule={selectedSchedule}
                            assignments={getShiftAssignees(shift)}
                            currentEmployeeId={currentEmployeeId}
                            getEmployee={getEmployee}
                            getPosition={getPosition}
                            disabled={isDateTooOld}
                            onSwapShift={() => {
                              setSelectedShift(shift)
                              setShowSwapModal(true)
                            }}
                            onRequestTimeOff={() => {
                              setSelectedShift(shift);
                              setShowRequestOffModal(true);
                            }}
                          />
                        ))}
                      </>
                    );
                  })()}
                </>
              )}
            </Box>
          )}

          {/* Open Shifts Tab */}
          {activeTab === 2 && (
            <Box>
              {openShiftsWithPriority.length === 0 ? (
                <Alert severity="info" icon={<CheckCircleIcon />}>
                  No open shifts matching your positions available for this day.
                </Alert>
              ) : (
                <>
                  {(() => {
                    const filteredOpenShifts = sortShiftsByName(filterShifts(openShiftsWithPriority));
                    return (
                      <>
                        {filteredOpenShifts.length === 0 && (
                          <Alert severity="info" sx={{ mb: 2 }}>
                            No shifts match your search and filter criteria.
                          </Alert>
                        )}
                        {filteredOpenShifts.length > 0 && (
                          <Alert severity="info" sx={{ mb: 2 }}>
                            {filteredOpenShifts.length} shift{filteredOpenShifts.length !== 1 ? 's have' : ' has'} open spots.
                          </Alert>
                        )}
                        {filteredOpenShifts.map(shift => (
                          <ShiftCard
                            key={shift.shiftId}
                            shift={shift}
                            type="open"
                            assignments={getShiftAssignmentForDate(shift.shiftId)}
                            selectedSchedule={selectedSchedule}
                            currentEmployeeId={currentEmployeeId}
                            getEmployee={getEmployee}
                            getPosition={getPosition}
                            disabled={isDateTooOld}
                          />
                        ))}
                      </>
                    );
                  })()}
                </>
              )}
            </Box>
          )}

          {/* Canceled Shifts Tab */}
          {activeTab === 3 && (
            <Box>
              {canceledShifts.length === 0 ? (
                <Alert severity="info" icon={<CheckCircleIcon />}>
                  No shifts have been canceled for this day.
                </Alert>
              ) : (
                <>
                  {(() => {
                    const filteredCanceledShifts = sortShiftsByName(filterShifts(canceledShifts));
                    return (
                      <>
                        {filteredCanceledShifts.length === 0 && (
                          <Alert severity="info" sx={{ mb: 2 }}>
                            No shifts match your search and filter criteria.
                          </Alert>
                        )}
                        {filteredCanceledShifts.length > 0 && (
                          <Alert severity="warning" icon={<EventBusyIcon />} sx={{ mb: 2 }}>
                            {filteredCanceledShifts.length} canceled shift{filteredCanceledShifts.length !== 1 ? 's' : ''}.
                          </Alert>
                        )}
                        {filteredCanceledShifts.map(cancelShift => {
                          const originalShift = shifts.find(s => s.shiftId === ('shiftReferenceId' in cancelShift ? cancelShift.shiftReferenceId : null));
                          return (
                            <ShiftCard
                              key={cancelShift.shiftId}
                              shift={originalShift || cancelShift}
                              type="other"
                              selectedSchedule={selectedSchedule}
                              currentEmployeeId={currentEmployeeId}
                              assignments={getShiftAssignees(originalShift || cancelShift)}
                              getEmployee={getEmployee}
                              getPosition={getPosition}
                              isCanceled={true}
                              onRevertCancel={(shift) => {
                                setShiftToRevertCancel(shift);
                                setShowRevertCancelDialog(true);
                              }}
                              disabled={isDateTooOld}
                            />
                          );
                        })}
                      </>
                    );
                  })()}
                </>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Pick Up Shift Confirmation Dialog */}
      <Dialog open={showPickUpDialog} onClose={() => setShowPickUpDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'success.light' }}>
              <AddIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight="bold">
                Confirm Pick Up Shift
              </Typography>
              <Typography variant="caption" color="text.secondary">
                You're about to add this shift to your schedule
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {shiftToPickUp && (
            <>
              <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'success.50', borderColor: 'success.200' }}>
                <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                  Shift to Pick Up
                </Typography>
                <Typography variant="h6" fontWeight={600}>
                  {shiftToPickUp.shiftName}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {formatShiftTime(shiftToPickUp.startTime)} - {formatShiftTime(shiftToPickUp.endTime)} • {getShiftDuration(shiftToPickUp)}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                  <Chip
                    label={getPosition(shiftToPickUp.position)?.positionName || getPosition(shiftToPickUp.position)?.name || 'Unknown Position'}
                    size="small"
                  />
                  {shiftToPickUp.isRecurring && (
                    <Chip
                      label="Recurring"
                      size="small"
                      color="info"
                      variant="outlined"
                    />
                  )}
                </Box>

                {/* Show spots remaining */}
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {getSpotsRemaining(shiftToPickUp)} spot{getSpotsRemaining(shiftToPickUp) !== 1 ? 's' : ''} remaining after you join
                </Typography>
              </Paper>

              {/* Show conflict warning if exists */}
              {hasConflictWithMyShifts(shiftToPickUp) && (
                <Alert severity="warning" sx={{ mb: 2 }} icon={<WarningIcon />}>
                  <Typography variant="body2" fontWeight={600} gutterBottom>
                    Schedule Conflict Detected
                  </Typography>
                  <Typography variant="body2">
                    This shift overlaps with one of your existing shifts on this day. Please review your schedule carefully.
                  </Typography>
                </Alert>
              )}

              {/* Show current day workload */}
              {myShiftsWithPriority.length > 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight={600} gutterBottom>
                    Your Current Schedule for {moment(date).format('MMM D')}
                  </Typography>
                  {myShiftsWithPriority.map((shift, idx) => (
                    <Typography key={shift.shiftId} variant="caption" display="block">
                      {idx + 1}. {shift.shiftName} ({formatShiftTime(shift.startTime)} - {formatShiftTime(shift.endTime)})
                    </Typography>
                  ))}
                </Alert>
              )}

              {/* Show shift notes if any */}
              {shiftToPickUp.notes && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight={600} gutterBottom>
                    Shift Notes
                  </Typography>
                  <Typography variant="body2">
                    {shiftToPickUp.notes}
                  </Typography>
                </Alert>
              )}

              <Typography variant="body2" color="text.secondary">
                By picking up this shift, you confirm that you're available to work and understand the responsibilities.
                {shiftToPickUp.isRecurring ? ' This is a recurring shift that will be added to your regular schedule.' : ' This is a one-time shift.'}
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setShowPickUpDialog(false)} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmPickUp}
            variant="contained"
            color="success"
            startIcon={<AddIcon />}
          >
            Confirm Pick Up
          </Button>
        </DialogActions>
      </Dialog>

      {/* Swap Shift Modal */}
      <SwapShiftModal
        open={showSwapModal}
        onClose={() => { setShowSwapModal(false); setSelectedShift(null); }}
        shift={selectedShift}
        currentEmployeeId={currentEmployeeId}
        employees={employees}
        positions={positions}
        onSubmitSwapRequest={handleSwapSubmit}
        selectedSchedule={selectedSchedule}
        selectedDate={date}
      />

      {/* Request Off Modal */}
      <RequestOffModal
        open={showRequestOffModal}
        onClose={() => { setShowRequestOffModal(false); setSelectedShift(null); }}
        shift={selectedShift}
        currentEmployeeId={currentEmployeeId}
        employees={employees}
        positions={positions}
        availableShifts={shifts}
        onSubmitRequestOff={handleRequestTimeOff}
        selectedSchedule={selectedSchedule}
        selectedDate={date}
      />

      {/* Revert Cancellation Confirmation Dialog */}
      <Dialog open={showRevertCancelDialog} onClose={() => setShowRevertCancelDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'warning.light' }}>
              <EventBusyIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight="bold">
                Revert Shift Cancellation?
              </Typography>
              <Typography variant="caption" color="text.secondary">
                This will restore the shift to the schedule
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {shiftToRevertCancel && (
            <>
              <Alert severity="warning" sx={{ mb: 2 }} icon={<WarningIcon />}>
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  Are you sure you want to revert this cancellation?
                </Typography>
                <Typography variant="body2">
                  The shift <strong>{shiftToRevertCancel.shiftName}</strong> will be restored and become available again.
                </Typography>
              </Alert>

              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="caption" color="text.secondary" gutterBottom display="block" fontWeight={600}>
                  Shift Details
                </Typography>
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="body2" gutterBottom>
                    <strong>Name:</strong> {shiftToRevertCancel.shiftName}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Time:</strong> {formatShiftTime(shiftToRevertCancel.startTime)} - {formatShiftTime(shiftToRevertCancel.endTime)}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Position:</strong> {getPosition(shiftToRevertCancel.position)?.positionName || 'Unknown'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Staff Needed:</strong> {shiftToRevertCancel.staffNeeded}
                  </Typography>
                </Box>
              </Paper>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setShowRevertCancelDialog(false)} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={() => {
              try {
                if (shiftToRevertCancel) {
                  deleteShift(shiftToRevertCancel.scheduleId, shiftToRevertCancel.shiftId).then((response) => {
                    setShifts(prevShifts => prevShifts.filter(s => s.shiftId !== shiftToRevertCancel.shiftId));
                    setShowRevertCancelDialog(false);
                    setShiftToRevertCancel(null);
                  })
                  toast.showSuccess('Shift cancellation reverted successfully.');
                }
              } catch (error) {
                toast.showError('Failed to revert shift cancellation. Please try again.');
              }
            }}
            variant="contained"
            color="error"
          >
            Revert Cancellation
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Shift Confirmation Dialog */}
      <Dialog open={showDeleteConfirmDialog} onClose={() => setShowDeleteConfirmDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'error.light' }}>
              <WarningIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight="bold">
                Delete Shift
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Choose how to delete this shift
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {shiftToDelete && (
            <>
              <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
                <Typography variant="caption" color="text.secondary" gutterBottom display="block" fontWeight={600}>
                  Shift Details
                </Typography>
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="body2" gutterBottom>
                    <strong>Name:</strong> {shiftToDelete.shiftName}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Time:</strong> {formatShiftTime(shiftToDelete.startTime)} - {formatShiftTime(shiftToDelete.endTime)}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Position:</strong> {getPosition(shiftToDelete.position)?.positionName || 'Unknown'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Staff Needed:</strong> {shiftToDelete.staffNeeded}
                  </Typography>
                </Box>
              </Paper>

              {shiftToDelete.isRecurring && (
                <>
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 2 }}>
                    This is a recurring shift. How would you like to delete it?
                  </Typography>
                  <Stack spacing={1.5}>
                    <Button
                      fullWidth
                      variant="outlined"
                      color="error"
                      onClick={() => {
                        try {
                          let cancelShiftId = uuidv4()
                          putShiftCancel(shiftToDelete.scheduleId, shiftToDelete.shiftId, shiftToDelete.shiftName, shiftToDelete.daysOfWeek || [],
                            shiftToDelete.startTime, shiftToDelete.endTime, shiftToDelete.position, shiftToDelete.staffNeeded, shiftToDelete.assignedStaff,
                            shiftToDelete.isRecurring, date, shiftToDelete.notes, cancelShiftId).then((response) => {
                              let cancelShift = {
                                ...shiftToDelete,
                                shiftId: cancelShiftId,
                                shiftDate: date,
                                cancelShiftId: cancelShiftId,
                                shiftReferenceId: shiftToDelete.shiftId
                              }
                              setShifts(prevShifts => [...prevShifts, cancelShift]);
                              setShowDeleteConfirmDialog(false);
                              setShiftToDelete(null);
                              toast.showSuccess('Shift occurrence deleted successfully.');
                            })
                        } catch (error) {
                          toast.showError('Failed to delete shift. Please try again.');
                        }
                      }}
                    >
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          Delete Only This Occurrence
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Cancel only the {moment(date).format('MMM D, YYYY')} instance
                        </Typography>
                      </Box>
                    </Button>
                    <Button
                      fullWidth
                      variant="contained"
                      color="error"
                      onClick={() => {
                        try {
                          let cancelShifts = allShifts.filter(s => s.shiftReferenceId === shiftToDelete.shiftId);
                          cancelShifts.forEach(shift => {
                            deleteShift(shift.scheduleId, shift.shiftId);
                          })
                          deleteShift(shiftToDelete.scheduleId, shiftToDelete.shiftId).then((response) => {
                            setShifts(prevShifts => prevShifts.filter(s => s.shiftId !== shiftToDelete.shiftId
                              && !cancelShifts.some(cs => cs.shiftId === s.shiftId)
                            ));
                            setShowDeleteConfirmDialog(false);
                            setShiftToDelete(null);
                            toast.showSuccess('Recurring shift deleted successfully.');
                          })
                        } catch (error) {
                          toast.showError('Failed to delete shift. Please try again.');
                        }
                      }}
                    >
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          Delete All Occurrences
                        </Typography>
                        <Typography variant="caption" display="block">
                          Permanently delete this recurring shift
                        </Typography>
                      </Box>
                    </Button>
                  </Stack>
                </>
              )}
              {!shiftToDelete.isRecurring && (
                <Typography variant="body2" color="text.secondary">
                  This shift will be permanently deleted from the schedule.
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setShowDeleteConfirmDialog(false)} variant="outlined">
            Cancel
          </Button>
          {shiftToDelete && !shiftToDelete.isRecurring && (
            <Button
              onClick={() => {
                try {
                  deleteShift(shiftToDelete.scheduleId, shiftToDelete.shiftId).then((response) => {
                    setShifts(prevShifts => prevShifts.filter(s => s.shiftId !== shiftToDelete.shiftId));
                    setShowDeleteConfirmDialog(false);
                    setShiftToDelete(null);
                    toast.showSuccess('Shift deleted successfully.');
                  })
                } catch (error) {
                  toast.showError('Failed to delete shift. Please try again.');
                }
              }}
              variant="contained"
              color="error"
            >
              Delete
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EmployeeDayModal;